import {
  ActionBar,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
  InputGroupV2,
  ModalV2,
  Text,
  TextInputV2,
} from '@equip.health/ui';
import { useState } from 'react';
import {
  useCreateQoLGoalMutation,
  useCreateCarePlanMutation,
} from '~/store/services/carePlan';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

const TOP_LEVEL_ID = 'add-qol-goal-modal';

interface AddQoLGoalModalProps {
  treatmentEpisodeId: string;
  treatmentPathwayId?: string;
  noteExternalId?: string;
  onClose: () => void;
}

const AddQoLGoalModal = ({
  treatmentEpisodeId,
  treatmentPathwayId,
  noteExternalId,
  onClose,
}: AddQoLGoalModalProps) => {
  const { trackEvent } = useAnalytics();
  const [title, setTitle] = useState('');
  const [isShowDescription, setIsShowDescription] = useState(false);
  const [description, setDescription] = useState('');

  const { showErrorToast } = useErrorToast();

  const [createTreatmentPathway, { isLoading: isCreatePathwayLoading }] =
    useCreateCarePlanMutation();
  const [createQoLGoal, { isLoading: isCreateQolGoalLoading }] =
    useCreateQoLGoalMutation();

  const handleSave = async () => {
    let newTreatmentPathwayId = treatmentPathwayId;
    if (!treatmentPathwayId) {
      try {
        const treatmentPathwayResponse = await createTreatmentPathway({
          includeCurrent: false,
          statusCode: CarePlanStatus.ACTIVE,
          treatmentEpisodeId,
        }).unwrap();
        newTreatmentPathwayId = treatmentPathwayResponse.id;
      } catch (error) {
        showErrorToast(
          GlobalErrorCode.CREATE_TREATMENT_PATHWAY,
          error.status,
          error,
        );
        return;
      }
    }

    try {
      await createQoLGoal({
        description,
        statusCode: CarePlanGoalStatus.NOT_STARTED,
        title,
        treatmentEpisodeId,
        carePlanId: newTreatmentPathwayId,
        noteExternalId: noteExternalId ?? null,
      }).unwrap();
    } catch (error) {
      showErrorToast(GlobalErrorCode.CREATE_QOL_GOAL, error.status, error);
    }

    onClose();
  };

  const handleRemoveDescription = () => {
    setIsShowDescription(false);
    setDescription('');
  };

  return (
    <ModalV2
      bodyOverflow="visible"
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={() => {
              onClose();
              trackEvent(CarePlanEvents.clickOnCancelButtonInAddQoLGoalModal);
            }}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__save`}
            isDisabled={!title}
            isLoading={isCreatePathwayLoading || isCreateQolGoalLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              handleSave();
              trackEvent(
                CarePlanEvents.clickOnAddToCarePlanButtonInAddQoLGoalModal,
              );
            }}
          >
            Add to care plan
          </FireflyButton>
        </ChakraHStack>
      }
      isOpen
      onClose={() => {
        onClose();
        trackEvent(CarePlanEvents.clickOnCloseIconInAddQoLGoalModal);
      }}
      size="5xl"
      title="Add quality of life goal"
    >
      <Text marginBottom="24px" variant="body">
        Please provide more information about the goal you'd like to add
      </Text>

      <ChakraBox
        border="1px"
        borderColor="neutral.line"
        borderRadius="12px"
        marginBottom="24px"
        paddingX="18px"
        paddingY="12px"
      >
        <ChakraBox marginBottom="12px">
          <InputGroupV2 id={`${TOP_LEVEL_ID}_title-input-group`}>
            <TextInputV2
              id={`${TOP_LEVEL_ID}_title-input`}
              onChange={setTitle}
              onFocus={() =>
                trackEvent(
                  CarePlanEvents.clickIntoQoLTitleTextboxInAddGoalModal,
                )
              }
              placeholder="Please provide a title for this goal"
              value={title}
            />
          </InputGroupV2>
        </ChakraBox>
        {!isShowDescription && (
          <FireflyButton
            id={`${TOP_LEVEL_ID}_add-desc-btn`}
            leftIcon={FAIconName.plusRegular}
            onClick={() => {
              setIsShowDescription(true);
              trackEvent(
                CarePlanEvents.clickOnAddGoalDescriptionInAddQoLGoalModal,
              );
            }}
            variant="tertiary"
          >
            Add goal description
          </FireflyButton>
        )}
        {isShowDescription && (
          <ChakraFlex alignItems="center" gap="12px">
            <InputGroupV2 id={`${TOP_LEVEL_ID}_desc-input-group`}>
              <TextInputV2
                id={`${TOP_LEVEL_ID}_desc-input`}
                onChange={setDescription}
                onFocus={() =>
                  trackEvent(
                    CarePlanEvents.clickIntoQoLGoalDescriptionTextboxInAddQoLGoalModal,
                  )
                }
                placeholder="Please provide a description for this goal"
                value={description}
              />
            </InputGroupV2>
            <ChakraBox>
              <FireflyIconButton
                color="critical.base"
                icon={FAIconName.trashRegular}
                id={`${TOP_LEVEL_ID}_delete-desc-btn`}
                onClick={handleRemoveDescription}
                variant="tertiary"
              />
            </ChakraBox>
          </ChakraFlex>
        )}
      </ChakraBox>

      <ActionBar
        barIcon={FAIconName.exclamationCircleRegular}
        id={`${TOP_LEVEL_ID}_note-info`}
        text="Please note, this goal will be visible on the care plan but will not be tied to any problems, measures, or interventions."
        variant="info"
      />
    </ModalV2>
  );
};

export default AddQoLGoalModal;
